import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { extractUsername } from "@/utils/extractUsername";
import {
  Filter,
  Loader2,
  RefreshCw,
  Search,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { addFriend, getFriendSuggestions } from "../../api/friend.api";
import { UserProfileCard } from "../../components";
import { FriendSuggestion } from "../../types/friend.type";

// Compact Suggestion Card for Sidebar
interface CompactSuggestionCardProps {
  suggestion: FriendSuggestion;
  onSelect: (suggestion: FriendSuggestion) => void;
  onAddFriend: (userId: number) => void;
  onDismiss: (suggestionId: number) => void;
  isSelected?: boolean;
  isLoading?: boolean;
}

const CompactSuggestionCard: React.FC<CompactSuggestionCardProps> = ({
  suggestion,
  onSelect,
  onAddFriend,
  onDismiss,
  isSelected = false,
  isLoading = false,
}) => {
  const displayName =
    suggestion.name || extractUsername(suggestion.email || "");

  return (
    <div
      className={`group relative p-3 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-md ${
        isSelected
          ? "bg-blue-50 border-blue-200 shadow-sm"
          : "bg-white border-gray-100 hover:border-gray-200"
      }`}
      onClick={() => onSelect(suggestion)}
    >
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0">
          <Avatar className="w-12 h-12">
            <AvatarImage
              src={suggestion.photo}
              alt={displayName}
              className="object-cover"
            />
            <AvatarFallback className="text-sm font-semibold bg-gradient-to-br from-blue-100 to-purple-100 text-gray-700">
              {displayName.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
        </div>

        <div className="flex-1 min-w-0">
          <h4
            className={`font-semibold  line-clamp-1 ${
              isSelected ? "text-blue-700" : "text-gray-900"
            }`}
          >
            {displayName}
          </h4>
          {suggestion.username && (
            <p className="text-xs text-gray-500 line-clamp-1">
              @{extractUsername(suggestion.username)}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            size="sm"
            variant="ghost"
            className="w-8 h-8 p-0 text-blue-600 hover:bg-blue-100"
            onClick={(e) => {
              e.stopPropagation();
              onAddFriend(suggestion.id);
            }}
            disabled={isLoading}
          >
            <UserPlus className="w-3 h-3" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="w-8 h-8 p-0 text-gray-400 hover:bg-gray-100"
            onClick={(e) => {
              e.stopPropagation();
              onDismiss(suggestion.id);
            }}
          >
            <X className="w-3 h-3" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const FriendSuggestionsScreen: React.FC = () => {
  const { toast } = useToast();
  const [suggestions, setSuggestions] = useState<FriendSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingUserId, setLoadingUserId] = useState<number | null>(null);
  const [selectedSuggestion, setSelectedSuggestion] =
    useState<FriendSuggestion | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [isLoadMore, setIsLoadMore] = useState(true);
  const [totalElement, setTotalElement] = useState(0);

  const loadSuggestions = async (pageNumber = 1) => {
    try {
      if (pageNumber === 1) {
        setIsLoading(true);
      }

      const response = await getFriendSuggestions({
        keyword: searchQuery.trim() || undefined,
        pageNumber,
        pageSize,
      });

      if (response.data) {
        setTotalElement(response.totalElements || 0);

        if (pageNumber === 1) {
          setSuggestions(response.data);
          // Auto-select first suggestion if none selected
          if (response.data.length > 0 && !selectedSuggestion) {
            setSelectedSuggestion(response.data[0]);
          }
        } else {
          setSuggestions((prev) => [...prev, ...response.data]);
        }

        // Update hasMore based on response
        if (!response.data.length || response.data.length < pageSize) {
          setIsLoadMore(false);
        }
      }
    } catch (error: any) {
      console.error("Error loading suggestions:", error);
      toast({
        title: "Thất bại!",
        variant: "error",
        description: "Không thể tải gợi ý kết bạn. Vui lòng thử lại.",
      });
    } finally {
      if (pageNumber === 1) {
        setIsLoading(false);
      }
    }
  };

  // Load more for infinite scroll
  const loadMoreSuggestions = async () => {
    const nextPage = page + 1;
    setPage(nextPage);
    await loadSuggestions(nextPage);
  };

  // Initial load effect
  useEffect(() => {
    setPage(1);
    setIsLoadMore(true);
    setSelectedSuggestion(null);
    loadSuggestions(1);
  }, [searchQuery]);

  const handleAddFriend = useCallback(
    async (userId: number) => {
      try {
        setLoadingUserId(userId);
        await addFriend(userId);

        // Remove from suggestions after adding
        setSuggestions((prev) => prev.filter((s) => s.id !== userId));
        setTotalElement((prev) => prev - 1);

        // Clear selected if it was the added friend
        if (selectedSuggestion?.id === userId) {
          setSelectedSuggestion(null);
        }

        toast({
          title: "Thành công!",
          description: "Đã gửi lời mời kết bạn.",
        });
      } catch (error: any) {
        console.error("Error adding friend:", error);
        toast({
          title: "Thất bại!",
          variant: "error",
          description: "Không thể gửi lời mời kết bạn. Vui lòng thử lại.",
        });
      } finally {
        setLoadingUserId(null);
      }
    },
    [selectedSuggestion?.id, toast]
  );

  const handleDismiss = useCallback(
    (suggestionId: number) => {
      setSuggestions((prev) => prev.filter((s) => s.id !== suggestionId));
      setTotalElement((prev) => prev - 1);

      // Clear selected if it was dismissed
      if (selectedSuggestion?.id === suggestionId) {
        setSelectedSuggestion(null);
      }
    },
    [selectedSuggestion?.id]
  );

  const handleRefresh = useCallback(() => {
    setPage(1);
    setIsLoadMore(true);
    setSelectedSuggestion(null);
    loadSuggestions(1);
  }, []);

  const handleSuggestionSelect = useCallback((suggestion: FriendSuggestion) => {
    setSelectedSuggestion(suggestion);
  }, []);

  // Convert FriendSuggestion to Friend format for UserProfileCard
  const convertToFriend = (suggestion: FriendSuggestion) => ({
    id: suggestion.id,
    name: suggestion.name,
    email: suggestion.email,
    username: suggestion.username,
    photo: suggestion.photo,
    biography: undefined, // suggestions don't have biography
    isOnline: false, // suggestions don't have online status
  });

  const handleMessage = useCallback((userId: number) => {
    console.log("Navigate to message with user:", userId);
    // TODO: Implement navigation to messages
  }, []);

  const handleCall = useCallback((userId: number) => {
    console.log("Start call with user:", userId);
    // TODO: Implement call functionality
  }, []);

  const handleVideoCall = useCallback((userId: number) => {
    console.log("Start video call with user:", userId);
    // TODO: Implement video call functionality
  }, []);

  const handleUnfriend = useCallback(
    async (userId: number) => {
      // This converts to add friend since it's a suggestion
      await handleAddFriend(userId);
    },
    [handleAddFriend]
  );

  return (
    <div className="flex h-screen pt-[70px] gap-4">
      {/* Left Sidebar - Suggestions List */}
      <div className="w-80 bg-white flex flex-col border-r border-l border-gray-50">
        {/* Header */}
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold text-gray-900">Gợi ý kết bạn</h1>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleRefresh}
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Tìm kiếm theo tên, email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-9 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          {/* Stats */}
          {totalElement > 0 && (
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs">
                <Users className="w-3 h-3 mr-1" />
                {totalElement} gợi ý
              </Badge>
              {searchQuery && (
                <Badge
                  variant="outline"
                  className="border-gray-200 text-gray-600 text-xs"
                >
                  <Filter className="w-3 h-3 mr-1" />"{searchQuery}"
                </Badge>
              )}
            </div>
          )}
        </div>

        {/* Suggestions List */}
        <div className="flex-1 overflow-hidden">
          <div
            id="suggestions-scrollable"
            className="h-full overflow-y-auto px-4 pb-4"
          >
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                <span className="text-sm text-gray-500">Đang tải...</span>
              </div>
            ) : suggestions.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <h3 className="font-medium text-gray-900 mb-2">
                  {searchQuery ? "Không tìm thấy" : "Không có gợi ý"}
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  {searchQuery
                    ? `Không tìm thấy gợi ý nào với "${searchQuery}"`
                    : "Hiện tại chưa có gợi ý kết bạn"}
                </p>
                {searchQuery && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSearchQuery("")}
                  >
                    Xóa bộ lọc
                  </Button>
                )}
              </div>
            ) : (
              <InfiniteScroll
                style={{
                  height: "unset",
                  overflow: "unset",
                }}
                dataLength={suggestions.length}
                next={loadMoreSuggestions}
                hasMore={isLoadMore}
                loader={
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    <span className="text-sm text-gray-500">
                      Đang tải thêm...
                    </span>
                  </div>
                }
                scrollableTarget="suggestions-scrollable"
              >
                <div className="space-y-2">
                  {suggestions.map((suggestion) => (
                    <CompactSuggestionCard
                      key={suggestion.id}
                      suggestion={suggestion}
                      onSelect={handleSuggestionSelect}
                      onAddFriend={handleAddFriend}
                      onDismiss={handleDismiss}
                      isSelected={selectedSuggestion?.id === suggestion.id}
                      isLoading={loadingUserId === suggestion.id}
                    />
                  ))}
                </div>
              </InfiniteScroll>
            )}
          </div>
        </div>
      </div>

      {/* Right Content - User Profile */}
      <div className="flex-1 flex flex-col">
        {selectedSuggestion ? (
          <div className="p-6 overflow-y-auto">
            <UserProfileCard
              user={convertToFriend(selectedSuggestion)}
              onMessage={handleMessage}
              onCall={handleCall}
              onVideoCall={handleVideoCall}
              onUnfriend={handleUnfriend}
              onAddFriend={handleAddFriend}
            />
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserPlus className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Chọn một gợi ý kết bạn
              </h3>
              <p className="text-gray-500 max-w-sm">
                Chọn một người từ danh sách gợi ý bên trái để xem thông tin chi
                tiết và gửi lời mời kết bạn.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FriendSuggestionsScreen;
